import './HistoryChart.less';
import React from 'react';
import { inject, observer } from 'mobx-react';
import { Recharts, AppLocaleProvider } from 'sd-ui-components';
import CustomizedLegend from './CustomizedLegend';
import CustomizedLabel from './CustomizedLabel';
import CustomizedContainer from './CustomizedContainer';

const { injectIntl } = AppLocaleProvider;

const { ResponsiveContainer, LineChart, Line, XAxis, Label, YAxis } = Recharts;

@inject('SafetyStore')
@observer
class HistoryChart extends React.Component {
  /**
   * Determines if 'edgeScore' points need to be added to the left side of the right history chart
   * or the right side of the left history chart.
   *
   * This is the case when the "edge" point is null, leading to a blank spot on the side of the chart.
   *
   * This method decorates the given array with 'edgeScore' data which is displayed by a special <Line/> component.
   */
  addConnectingLinePoints(array, historyData, startingIndex, endingIndex) {
    // If we are working on the last bit of data, get the left connecting line.
    if (endingIndex > historyData.length / 2) {
      return this.addLeftConnectingLinePoints(
        array,
        historyData,
        startingIndex
      );
    } else {
      return this.addRightConnectingLinePoints(
        array,
        historyData,
        startingIndex,
        endingIndex
      );
    }
  }

  /**
   * Adds "edgeScore" points to the left edge of the chart by interpolating
   * between the two closest non-null points which surround the edge.
   * @param {*} array slice of the history data array.
   * @param {*} startingIndex The index which contains the 'edge' point (the leftmost displayed array index with respect to the full history data).
   */
  addLeftConnectingLinePoints(array, historyData, startingIndex) {
    let closestRightNonNullIndex = this.findClosestRightNonNullPoint(
      historyData,
      startingIndex
    );
    let closestLeftNonNullIndex = this.findClosestLeftNonNullPoint(
      historyData,
      startingIndex - 1
    );

    if (
      !this.hasNullEdgesToPopulate(
        historyData,
        closestLeftNonNullIndex,
        closestRightNonNullIndex
      )
    ) {
      return array;
    }

    const new_y = this.calculateNewYAtEdge(
      historyData,
      closestLeftNonNullIndex,
      closestRightNonNullIndex,
      startingIndex
    );

    /**
     * Because we adding LEFT connections, we can add the newly interpolated point at array[0].
     * The connecting point goes to the next non-null point in THIS array (which is a slice of the full history array).
     */

    // Guard against any weird indexes which could result from bad inputs.
    if (
      closestRightNonNullIndex - startingIndex < 0 ||
      closestRightNonNullIndex - startingIndex >= array.length
    ) {
      console.warn(
        'An error occurred when connecting null edges, an index was out of bounds.'
      );
      return;
    }

    array[0]['edgeScore'] = new_y;
    array[closestRightNonNullIndex - startingIndex]['edgeScore'] =
      historyData[closestRightNonNullIndex].driverScore;

    return array;
  }

  /**
   * @param {*} endingIndex EXCLUSIVE. This means that for a given array, ending index could be 6 and the array length is also 6.
   */
  addRightConnectingLinePoints(array, historyData, startingIndex, endingIndex) {
    let closestRightNonNullIndex = this.findClosestRightNonNullPoint(
      historyData,
      endingIndex
    );
    let closestLeftNonNullIndex = this.findClosestLeftNonNullPoint(
      historyData,
      endingIndex - 1
    );

    if (
      !this.hasNullEdgesToPopulate(
        historyData,
        closestLeftNonNullIndex,
        closestRightNonNullIndex
      )
    ) {
      return array;
    }

    const new_y = this.calculateNewYAtEdge(
      historyData,
      closestLeftNonNullIndex,
      closestRightNonNullIndex,
      endingIndex
    );

    /**
     * Because we are adding RIGHT connections, we can use the last element of the array to place the newly interpolated point.
     * The connecting point can be placed at the cloesest left non-null index, which will be the same as the full history array
     * because we are working with the leftmost slice.
     */

    // Guard against any weird indexes which could result from bad inputs.
    if (
      closestLeftNonNullIndex < 0 ||
      closestLeftNonNullIndex >= array.length
    ) {
      console.warn(
        'An error occurred when connecting null edges, an index was out of bounds.'
      );
      return;
    }

    array[array.length - 1]['edgeScore'] = new_y;
    array[closestLeftNonNullIndex]['edgeScore'] =
      historyData[closestLeftNonNullIndex].driverScore;

    return array;
  }

  /**
   * Starts at an index and works left until finding a non-null (valid) score point's index.
   * @param {int} startingIndex
   */
  findClosestLeftNonNullPoint(historyData, startingIndex) {
    while (
      startingIndex > 0 &&
      !this.props.SafetyStore.safetyScoreIsValid(
        historyData[startingIndex].driverScore
      )
    ) {
      startingIndex--;
    }
    return startingIndex;
  }

  /**
   * Starts at an index and works right until finding a non-null (valid) score point's index.
   * @param {int} startingIndex
   */
  findClosestRightNonNullPoint(historyData, startingIndex) {
    while (
      startingIndex < historyData.length - 1 &&
      !this.props.SafetyStore.safetyScoreIsValid(
        historyData[startingIndex].driverScore
      )
    ) {
      startingIndex++;
    }

    return startingIndex;
  }

  /**
   * Returns true if the chart has points at the edges which need to be populated.
   * This function checks that each of the given indexes are valid (sometimes all points are null)
   * AND that they are not adjacent points (there would be no gap to fill in this case).
   * @param {*} closestLeftNonNullIndex
   * @param {*} closestRightNonNullIndex
   */
  hasNullEdgesToPopulate(
    historyData,
    closestLeftNonNullIndex,
    closestRightNonNullIndex
  ) {
    return (
      this.props.SafetyStore.safetyScoreIsValid(
        historyData[closestRightNonNullIndex].driverScore
      ) &&
      this.props.SafetyStore.safetyScoreIsValid(
        historyData[closestLeftNonNullIndex].driverScore
      ) &&
      closestRightNonNullIndex - closestLeftNonNullIndex > 1
    );
  }

  /**
   * Calculates the value of Y at edge index by interpolating a line between the 2 closest non-null points.
   * @param {array} historyData full history data array
   * @param {int} closestLeftNonNullIndex
   * @param {int} closestRightNonNullIndex
   * @param {int} edgeIndex
   */
  calculateNewYAtEdge(
    historyData,
    closestLeftNonNullIndex,
    closestRightNonNullIndex,
    edgeIndex
  ) {
    const missingPoints = closestRightNonNullIndex - closestLeftNonNullIndex;
    const leftScore = historyData[closestLeftNonNullIndex].driverScore;
    const rightScore = historyData[closestRightNonNullIndex].driverScore;

    const m = (rightScore - leftScore) / missingPoints;
    // y will be where the point falls along the line at that new x.
    const new_x = edgeIndex;
    const new_y = this.pointSlope(
      m,
      new_x,
      closestRightNonNullIndex,
      rightScore
    );

    return new_y;
  }

  /**
   * Point slope line equation.
   */
  pointSlope(m, x, x1, y1) {
    return m * (x - x1) + y1;
  }

  render() {
    // Create and reference class using stroke
    const formatXAxis = tickItem =>
      this.props.intl.formatDate(new Date(tickItem), {
        month: '2-digit',
        day: '2-digit'
      });

    const doesChartHaveNullScores = this.props.SafetyStore.doesChartHaveNullScores(
      this.props.startingIndex,
      this.props.endingIndex
    );

    let filteredHistoryData = this.props.SafetyStore.getHistoryDataSlice(
      this.props.startingIndex,
      this.props.endingIndex
    );

    filteredHistoryData = this.addConnectingLinePoints(
      filteredHistoryData,
      this.props.SafetyStore.safetyData.historyPage,
      this.props.startingIndex,
      this.props.endingIndex
    );

    const historyMinMaxData = this.props.SafetyStore.historyScoreMinMax;
    const targetScores = filteredHistoryData.map(i => i.targetScore);

    return (
      <div className="safety-score-chart">
        <ResponsiveContainer height={236}>
          <LineChart
            data={filteredHistoryData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <XAxis
              dataKey="periodEnding"
              height={50}
              tickFormatter={formatXAxis}
            >
              <Label
                offset={5}
                position="insideBottomLeft"
                content={
                  <CustomizedLegend
                    doesChartHaveNullScores={doesChartHaveNullScores}
                  />
                }
                value="Target"
              />
            </XAxis>
            <YAxis
              type="number"
              domain={[
                historyMinMaxData.targetReferenceMinData,
                historyMinMaxData.targetReferenceMaxData
              ]}
              hide
            />
            <Line
              dataKey="targetScore"
              label={null}
              dot={null}
              isAnimationActive={false}
              type="step"
              className="safety-chart-target-line"
            />
            <Line
              dataKey={'edgeScore'}
              dot={null}
              label={null}
              connectNulls={true}
              isAnimationActive={false}
              type="linear"
              className="safety-chart-history-dotted-line"
            />
            <Line
              dataKey="driverScore"
              dot={props => (
                <CustomizedContainer
                  connectNulls={true}
                  {...props}
                  targetScores={targetScores}
                />
              )}
              label={props => (
                <CustomizedLabel
                  connectNulls={true}
                  {...props}
                  targetScores={targetScores}
                />
              )}
              isAnimationActive={false}
              connectNulls={true}
              type="linear"
              className="safety-chart-history-dotted-line"
            />
            <Line
              dataKey="driverScore"
              dot={props => (
                <CustomizedContainer
                  connectNulls={false}
                  {...props}
                  targetScores={targetScores}
                />
              )}
              label={props => (
                <CustomizedLabel
                  connectNulls={false}
                  {...props}
                  targetScores={targetScores}
                />
              )}
              isAnimationActive={false}
              type="linear"
              className="safety-chart-history-line"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }
}

export default injectIntl(HistoryChart);



// WEBPACK FOOTER //
// ./src/containers/Safety/HistoryChart/HistoryChart.jsx